import { useState, useRef } from "react";
import { Btn, Badge } from "./common";
import { Trash2, MousePointerClick } from "lucide-react";

const STATUS_STYLE = {
  tersedia: { fill: "rgba(16,185,129,0.35)", stroke: "#10b981" },
  booking: { fill: "rgba(245,158,11,0.4)", stroke: "#f59e0b" },
  terjual: { fill: "rgba(244,63,94,0.4)", stroke: "#f43f5e" },
  hold: { fill: "rgba(99,102,241,0.4)", stroke: "#6366f1" },
  dibangun: { fill: "rgba(6,182,212,0.4)", stroke: "#06b6d4" },
};

export default function SiteplanMap({ image, units, editable, onSavePolygon, onUnitClick }) {
  const [mapping, setMapping] = useState(false);
  const [draft, setDraft] = useState([]);
  const [assignUnit, setAssignUnit] = useState("");
  const svgRef = useRef(null);

  const toSvg = (pts) => pts.map((p) => `${p[0] * 1000},${p[1] * 1000}`).join(" ");

  const handleClick = (e) => {
    if (!mapping) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setDraft([...draft, [x, y]]);
  };

  const finish = () => {
    if (draft.length >= 3 && assignUnit) {
      onSavePolygon(assignUnit, draft);
    }
    setDraft([]);
    setAssignUnit("");
  };

  const unmapped = units.filter((u) => !u.polygon || u.polygon.length < 3);

  return (
    <div data-testid="siteplan-map">
      {editable && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {!mapping ? (
            <Btn tone="amber" onClick={() => setMapping(true)} data-testid="start-mapping-button">
              <MousePointerClick className="w-4 h-4" /> Mode Mapping Poligon
            </Btn>
          ) : (
            <>
              <select
                className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100"
                value={assignUnit}
                onChange={(e) => setAssignUnit(e.target.value)}
                data-testid="mapping-unit-select"
              >
                <option value="">Pilih unit untuk area ini...</option>
                {unmapped.map((u) => <option key={u.id} value={u.id}>{u.block}-{u.number} ({u.type})</option>)}
              </select>
              <Btn onClick={finish} disabled={draft.length < 3 || !assignUnit} data-testid="mapping-finish-button">Selesai & Simpan ({draft.length} titik)</Btn>
              <Btn tone="ghost" onClick={() => setDraft([])} data-testid="mapping-clear-button"><Trash2 className="w-3.5 h-3.5" /> Ulangi</Btn>
              <Btn tone="ghost" onClick={() => { setMapping(false); setDraft([]); }} data-testid="mapping-cancel-button">Keluar Mode</Btn>
            </>
          )}
          {mapping && <span className="text-xs text-amber-300">Klik pada gambar untuk menambah titik poligon (min. 3), lalu pilih unit dan simpan.</span>}
        </div>
      )}
      <div className="relative rounded-xl border border-slate-800 overflow-hidden bg-slate-900">
        <img src={image} alt="Siteplan" className="w-full block select-none" draggable={false} data-testid="siteplan-image" />
        <svg
          ref={svgRef}
          viewBox="0 0 1000 1000"
          preserveAspectRatio="none"
          className={`absolute inset-0 w-full h-full ${mapping ? "cursor-crosshair" : ""}`}
          onClick={handleClick}
          data-testid="siteplan-svg-overlay"
        >
          {units.filter((u) => u.polygon && u.polygon.length >= 3).map((u) => {
            const st = STATUS_STYLE[u.status] || STATUS_STYLE.tersedia;
            const cx = u.polygon.reduce((s, p) => s + p[0], 0) / u.polygon.length * 1000;
            const cy = u.polygon.reduce((s, p) => s + p[1], 0) / u.polygon.length * 1000;
            return (
              <g key={u.id} onClick={(e) => { if (!mapping) { e.stopPropagation(); onUnitClick(u); } }} className={mapping ? "" : "cursor-pointer"} data-testid={`polygon-unit-${u.block}${u.number}`}>
                <polygon points={toSvg(u.polygon)} fill={st.fill} stroke={st.stroke} strokeWidth="3" vectorEffect="non-scaling-stroke" />
                <text x={cx} y={cy} textAnchor="middle" fontSize="22" fill="#fff" fontFamily="JetBrains Mono, monospace" style={{ pointerEvents: "none", textShadow: "0 1px 3px #000" }}>
                  {u.block}-{u.number}
                </text>
              </g>
            );
          })}
          {draft.length > 0 && (
            <g>
              <polygon points={toSvg(draft)} fill="rgba(6,182,212,0.25)" stroke="#06b6d4" strokeWidth="2" strokeDasharray="6 4" vectorEffect="non-scaling-stroke" />
              {draft.map((p, i) => <circle key={i} cx={p[0] * 1000} cy={p[1] * 1000} r="5" fill="#06b6d4" />)}
            </g>
          )}
        </svg>
      </div>
      <div className="flex gap-3 flex-wrap mt-3">
        <Badge tone="emerald">Tersedia: {units.filter((u) => u.status === "tersedia").length}</Badge>
        <Badge tone="amber">Booking: {units.filter((u) => u.status === "booking").length}</Badge>
        <Badge tone="cyan">Dibangun: {units.filter((u) => u.status === "dibangun" || u.under_construction).length}</Badge>
        <Badge tone="rose">Terjual: {units.filter((u) => u.status === "terjual").length}</Badge>
        {unmapped.length > 0 && <Badge tone="slate">Belum dipetakan: {unmapped.length} unit</Badge>}
      </div>
    </div>
  );
}
